import { useState, useCallback, useMemo } from 'react';
import { useHelpPreferences } from './useHelpPreferences';
import { tourDefinitions, TourDefinition, TourStepDefinition } from '../config/tourDefinitions';

// ============================================
// TYPES
// ============================================

export interface UseOnboardingTourReturn {
  /** Tour definition for the given profile (null if none found) */
  tour: TourDefinition | null;
  /** Current step object (null if tour inactive or no tour) */
  currentStep: TourStepDefinition | null;
  /** Zero-based index of the current step */
  currentStepIndex: number;
  /** Total number of steps in the tour */
  totalSteps: number;
  /** Whether the tour overlay is currently active/visible */
  isActive: boolean;
  /** Start the tour (set active) */
  start: () => void;
  /** Advance to the next step; completes tour if on last step */
  next: () => void;
  /** Go back to the previous step */
  previous: () => void;
  /** Skip/dismiss the tour entirely */
  skip: () => void;
  /** Check whether a given tour should be shown (not yet completed) */
  shouldShowTour: (tourId: string) => boolean;
}

// ============================================
// HOOK
// ============================================

export function useOnboardingTour(
  profileType: 'ADMIN' | 'CUSTOMER' | 'COMPLIANCE' | 'OPERATOR',
): UseOnboardingTourReturn {
  const { hasCompletedTour, markTourCompleted } = useHelpPreferences();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);

  // Find the tour matching the profile
  const tour = useMemo<TourDefinition | null>(
    () => tourDefinitions.find((t) => t.profileType === profileType) ?? null,
    [profileType],
  );

  const totalSteps = tour?.steps.length ?? 0;

  const currentStep = useMemo<TourStepDefinition | null>(
    () => (tour && isActive ? tour.steps[currentStepIndex] ?? null : null),
    [tour, isActive, currentStepIndex],
  );

  // Check if a tour should be shown (not yet completed)
  const shouldShowTour = useCallback(
    (tourId: string): boolean => !hasCompletedTour(tourId),
    [hasCompletedTour],
  );

  // Complete the tour (mark as done and deactivate)
  const completeTour = useCallback(() => {
    if (tour) {
      markTourCompleted(tour.id);
    }
    setIsActive(false);
    setCurrentStepIndex(0);
  }, [tour, markTourCompleted]);

  // Start the tour
  const start = useCallback(() => {
    if (!tour || tour.steps.length === 0) return;
    setCurrentStepIndex(0);
    setIsActive(true);
  }, [tour]);

  // Next step or complete
  const next = useCallback(() => {
    if (!tour) return;
    if (currentStepIndex >= totalSteps - 1) {
      completeTour();
    } else {
      setCurrentStepIndex((prev) => prev + 1);
    }
  }, [tour, currentStepIndex, totalSteps, completeTour]);

  // Previous step
  const previous = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  // Skip / dismiss
  const skip = useCallback(() => {
    completeTour();
  }, [completeTour]);

  return {
    tour,
    currentStep,
    currentStepIndex,
    totalSteps,
    isActive,
    start,
    next,
    previous,
    skip,
    shouldShowTour,
  };
}
